import React, { forwardRef, Ref, useImperativeHandle, useState } from "react";
import { useContext } from "react";
import TranslationsContext from "../../../infrastructure/context/translations/translations-context";
import ITranslationsContext from "../../../infrastructure/context/translations/translations-context.interface";
import MandatoryCheckbox from "./mandatory-checkbox";

type Props = {
  checkboxes: RegulationsCheckboxProps[];
  displayValidationMessages: boolean;
  validationMessageClass?: string;
};

export type RegulationsCheckboxProps = {
  id: string;
  labelHtml: string;
  additionalClassName?: string;
};

export interface MandatoryCheckboxesRefs {
  getSelectedCheckboxesList: () => Array<string>;
  allCheckboxesSelected: () => boolean;
}

type IsCheckboxSelected = {
  id: string;
  isSelected: boolean;
};

const MandatoryCheckboxesSection = forwardRef(
  (props: Props, ref: Ref<MandatoryCheckboxesRefs>): JSX.Element => {
    const { checkboxes, displayValidationMessages, validationMessageClass } =
      props;
    const translationsContext =
      useContext<ITranslationsContext>(TranslationsContext);
    const [selected, setCheckboxSelected] = useState<IsCheckboxSelected[]>(
      checkboxes.map(
        (val) =>
          ({
            id: val.id,
            isSelected: false,
            labelHtml: val.labelHtml,
          } as IsCheckboxSelected)
      )
    );

    useImperativeHandle(ref, () => ({
      getSelectedCheckboxesList(): Array<string> {
        return selected.filter((x) => x.isSelected).map((x) => x.id);
      },
      allCheckboxesSelected(): boolean {
        return !selected.some((x) => !x.isSelected);
      },
    }));

    const onCheckboxSelectionChange = (
      e: React.ChangeEvent<HTMLInputElement>,
      id: string
    ): void => {
      const checkboxes = [...selected];
      const checkbox = checkboxes.find((x) => x.id === id);

      if (!checkbox) {
        return;
      }

      checkbox.isSelected = e.target.checked;
      setCheckboxSelected(checkboxes);
    };

    return (
      <>
        {checkboxes.map((checkbox) => {
          const selectedCheckbox = selected.find((x) => x.id === checkbox.id);
          if (!selectedCheckbox) {
            return null;
          }

          return (
            <MandatoryCheckbox
              key={checkbox.id}
              isChecked={selectedCheckbox.isSelected}
              onSelectionChange={(
                e: React.ChangeEvent<HTMLInputElement>,
                id: string
              ) => onCheckboxSelectionChange(e, id)}
              id={checkbox.id}
              labelHtml={checkbox.labelHtml}
              displayValidationMessage={displayValidationMessages}
              translationsContext={translationsContext}
              validationMessageClass={validationMessageClass}
              additionalClassName={checkbox.additionalClassName}
            />
          );
        })}
      </>
    );
  }
);

export default MandatoryCheckboxesSection;
